import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type LoopStatus = 'active' | 'paused'

export interface WebhookEvent {
  id: number
  container_id: string
  event_type: string
  payload: Record<string, unknown>
  received_at: string
}

export interface AppEvent {
  id: number
  event_type: string
  message: string
  detail: string | null
  app_id: string | null
  container_id: string | null
  created_at: string
}

export interface TaskDecisionTask {
  id?: string
  skill?: string
  subtasks?: string[]
  status?: string
  requiredTaskIds?: string[]
  parentTaskId?: string
  created_at?: string
}

export interface TaskDecision {
  id: number
  app_id: string
  pending_count: number
  completed_count: number
  chosen_task: TaskDecisionTask | null
  created_at: string
}

export interface TaskDecisionDetail {
  id: number
  app_id: string
  pending_tasks: TaskDecisionTask[]
  completed_tasks: TaskDecisionTask[]
  chosen_task: TaskDecisionTask | null
  created_at: string
}

export interface StatusContainer {
  container_id: string
  name: string
  status: string
  app_id: string | null
  app_name: string | null
  fly_machine_id: string | null
  error_message: string | null
  created_at: string
  last_event_at: string | null
  active_task_id: string | null
}

interface StatusState {
  webhookEvents: WebhookEvent[]
  extraWebhookEvents: WebhookEvent[]
  appEvents: AppEvent[]
  recentContainers: StatusContainer[]
  containerIds: string[]
  taskDecisions: TaskDecision[]
  decisionDetails: Record<number, TaskDecisionDetail>
  containerFilter: string | null
  minContainers: number
  maxContainers: number
  containersPerApp: number
  loading: boolean
  initialized: boolean
  error: string | null
}

const initialState: StatusState = {
  webhookEvents: [],
  extraWebhookEvents: [],
  appEvents: [],
  recentContainers: [],
  containerIds: [],
  taskDecisions: [],
  decisionDetails: {},
  containerFilter: null,
  minContainers: 0,
  maxContainers: 10,
  containersPerApp: 1,
  loading: false,
  initialized: false,
  error: null,
}

export const fetchStatus = createAsyncThunk(
  'status/fetchStatus',
  async (containerId: string | null = null) => {
    const params = containerId ? `?containerId=${encodeURIComponent(containerId)}` : ''
    const response = await fetch(`/api/status${params}`)
    if (!response.ok) throw new Error('Failed to fetch status')
    return response.json() as Promise<{
      webhookEvents: WebhookEvent[]
      appEvents?: AppEvent[]
      recentContainers?: StatusContainer[]
      taskDecisions?: TaskDecision[]
      containerIds?: string[]
      minContainers: number
      maxContainers: number
      containersPerApp: number
    }>
  },
)

export const fetchTaskDecisionDetail = createAsyncThunk(
  'status/fetchTaskDecisionDetail',
  async (decisionId: number) => {
    const response = await fetch(`/api/status?decisionId=${decisionId}`)
    if (!response.ok) throw new Error('Failed to fetch decision detail')
    return response.json() as Promise<TaskDecisionDetail>
  },
)

export const fetchMoreWebhookEvents = createAsyncThunk(
  'status/fetchMoreWebhookEvents',
  async ({ before, containerId }: { before: string; containerId: string | null }) => {
    const params = new URLSearchParams({ before })
    if (containerId) params.set('containerId', containerId)
    const response = await fetch(`/api/status?${params.toString()}`)
    if (!response.ok) throw new Error('Failed to fetch more events')
    const data = await response.json() as { webhookEvents: WebhookEvent[] }
    return data.webhookEvents
  },
)

const statusSlice = createSlice({
  name: 'status',
  initialState,
  reducers: {
    setContainerFilter(state, action: { payload: string | null }) {
      state.containerFilter = action.payload
      state.extraWebhookEvents = []
      state.webhookEvents = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatus.pending, (state) => {
        if (!state.initialized) {
          state.loading = true
        }
        state.error = null
      })
      .addCase(fetchStatus.fulfilled, (state, action) => {
        state.loading = false
        state.initialized = true
        state.webhookEvents = [...action.payload.webhookEvents, ...state.extraWebhookEvents]
        if (action.payload.appEvents) {
          state.appEvents = action.payload.appEvents
        }
        if (action.payload.recentContainers) {
          state.recentContainers = action.payload.recentContainers
        }
        if (action.payload.taskDecisions) {
          state.taskDecisions = action.payload.taskDecisions
        }
        if (action.payload.containerIds) {
          state.containerIds = action.payload.containerIds
        }
        state.minContainers = action.payload.minContainers
        state.maxContainers = action.payload.maxContainers
        state.containersPerApp = action.payload.containersPerApp
      })
      .addCase(fetchStatus.rejected, (state, action) => {
        state.loading = false
        state.initialized = true
        state.error = action.error.message || 'Failed to fetch status'
      })
      .addCase(fetchTaskDecisionDetail.fulfilled, (state, action) => {
        state.decisionDetails[action.payload.id] = action.payload
      })
      .addCase(fetchMoreWebhookEvents.fulfilled, (state, action) => {
        state.extraWebhookEvents = [...state.extraWebhookEvents, ...action.payload]
        state.webhookEvents = [...state.webhookEvents, ...action.payload]
      })
  },
})

export const { setContainerFilter } = statusSlice.actions
export default statusSlice.reducer
